/* Technical indicators and chart-series transforms.
 *
 * Kept out of the chart component so the maths can be reasoned about — and
 * corrected — without touching rendering. Every function here takes bars and
 * returns a series aligned index-for-index with them, using `null` for the
 * warm-up period rather than a fabricated value.
 *
 * That null matters. An indicator that back-fills its warm-up with the first
 * computable value draws a flat line where it has no information, and a flat
 * line at the left edge of a chart reads as "the market did nothing", not as
 * "this indicator cannot say yet". A gap is the honest rendering.
 */
import type { Bar } from '@/types'

export type Series = (number | null)[]

const sum = (v: number[], from: number, to: number) => {
  let s = 0
  for (let i = from; i <= to; i++) s += v[i]!
  return s
}

// ---------------------------------------------------------------------------
// Moving averages
// ---------------------------------------------------------------------------

export function sma(v: number[], n: number): Series {
  return v.map((_, i) => (i < n - 1 ? null : sum(v, i - n + 1, i) / n))
}

export function ema(v: number[], n: number): Series {
  const k = 2 / (n + 1)
  const out: Series = []
  let prev: number | null = null
  v.forEach((x, i) => {
    if (i < n - 1) { out.push(null); return }
    // Seeded from an SMA, which is the conventional and reproducible choice:
    // seeding from the first sample makes the first few hundred values depend
    // on where the data window happened to start.
    prev = prev === null ? sum(v, i - n + 1, i) / n : x * k + prev * (1 - k)
    out.push(prev)
  })
  return out
}

/** Weighted moving average — linear weights, most recent heaviest. */
export function wma(v: number[], n: number): Series {
  const denom = (n * (n + 1)) / 2
  return v.map((_, i) => {
    if (i < n - 1) return null
    let acc = 0
    for (let k = 0; k < n; k++) acc += v[i - n + 1 + k]! * (k + 1)
    return acc / denom
  })
}

/** Session VWAP. Resets at each session boundary rather than running
 *  cumulatively across days — a "VWAP" that spans a week is not the number any
 *  trader means by VWAP, and using it to judge an entry would be wrong. */
export function vwap(bars: Bar[]): Series {
  let pv = 0
  let vol = 0
  let day = -1
  return bars.map((b) => {
    const d = new Date(b.time).getUTCDate()
    if (d !== day) { pv = 0; vol = 0; day = d }
    const typical = (b.high + b.low + b.close) / 3
    pv += typical * b.volume
    vol += b.volume
    return vol > 0 ? pv / vol : null
  })
}

// ---------------------------------------------------------------------------
// Bands and oscillators
// ---------------------------------------------------------------------------

export function bollinger(v: number[], n = 20, mult = 2) {
  const mid = sma(v, n)
  const upper: Series = []
  const lower: Series = []
  v.forEach((_, i) => {
    const m = mid[i]
    if (m == null) { upper.push(null); lower.push(null); return }
    let sq = 0
    for (let k = i - n + 1; k <= i; k++) sq += (v[k]! - m) ** 2
    const sd = Math.sqrt(sq / n)
    upper.push(m + mult * sd)
    lower.push(m - mult * sd)
  })
  return { mid, upper, lower }
}

export function rsi(v: number[], n = 14): Series {
  const out: Series = [null]
  let avgGain = 0
  let avgLoss = 0
  for (let i = 1; i < v.length; i++) {
    const ch = v[i]! - v[i - 1]!
    const gain = Math.max(ch, 0)
    const loss = Math.max(-ch, 0)
    if (i <= n) {
      avgGain += gain / n
      avgLoss += loss / n
      out.push(i === n ? 100 - 100 / (1 + (avgLoss === 0 ? 100 : avgGain / avgLoss)) : null)
    } else {
      // Wilder's smoothing, not a simple mean. The two give visibly different
      // values, and every published RSI level (30/70) is calibrated to this one.
      avgGain = (avgGain * (n - 1) + gain) / n
      avgLoss = (avgLoss * (n - 1) + loss) / n
      out.push(100 - 100 / (1 + (avgLoss === 0 ? 100 : avgGain / avgLoss)))
    }
  }
  return out
}

export function macd(v: number[], fast = 12, slow = 26, signal = 9) {
  const f = ema(v, fast)
  const s = ema(v, slow)
  const line: Series = v.map((_, i) =>
    f[i] == null || s[i] == null ? null : f[i]! - s[i]!)

  // The signal line is an EMA of the MACD line, which only exists once the
  // slow EMA does. Feeding the nulls in as zeros would drag the signal towards
  // zero for its whole warm-up.
  const defined = line.filter((x): x is number => x != null)
  const sigDefined = ema(defined, signal)
  const sig: Series = []
  let k = 0
  for (const x of line) sig.push(x == null ? null : sigDefined[k++]!)

  const hist: Series = line.map((x, i) =>
    x == null || sig[i] == null ? null : x - sig[i]!)
  return { line, signal: sig, histogram: hist }
}

export function stochastic(bars: Bar[], n = 14, d = 3) {
  const k: Series = bars.map((_, i) => {
    if (i < n - 1) return null
    let hi = -Infinity
    let lo = Infinity
    for (let j = i - n + 1; j <= i; j++) {
      hi = Math.max(hi, bars[j]!.high)
      lo = Math.min(lo, bars[j]!.low)
    }
    return hi === lo ? 50 : ((bars[i]!.close - lo) / (hi - lo)) * 100
  })
  const defined = k.filter((x): x is number => x != null)
  const dDefined = sma(defined, d)
  const dLine: Series = []
  let idx = 0
  for (const x of k) dLine.push(x == null ? null : dDefined[idx++]!)
  return { k, d: dLine }
}

/** Average true range — Wilder-smoothed. */
export function atr(bars: Bar[], n = 14): Series {
  const tr: number[] = bars.map((b, i) => {
    if (i === 0) return b.high - b.low
    const pc = bars[i - 1]!.close
    return Math.max(b.high - b.low, Math.abs(b.high - pc), Math.abs(b.low - pc))
  })
  const out: Series = []
  let prev: number | null = null
  tr.forEach((x, i) => {
    if (i < n - 1) { out.push(null); return }
    prev = prev === null ? sum(tr, i - n + 1, i) / n : (prev * (n - 1) + x) / n
    out.push(prev)
  })
  return out
}

/** Supertrend: an ATR band that flips side when price closes through it.
 *  Returns the line plus the direction, so the chart can colour each segment
 *  by trend rather than drawing one ambiguous line. */
export function supertrend(bars: Bar[], n = 10, mult = 3) {
  const a = atr(bars, n)
  const line: Series = []
  const dir: (1 | -1 | null)[] = []
  let upper = 0
  let lower = 0
  let trend: 1 | -1 = 1

  bars.forEach((b, i) => {
    if (a[i] == null) { line.push(null); dir.push(null); return }
    const mid = (b.high + b.low) / 2
    const basicUpper = mid + mult * a[i]!
    const basicLower = mid - mult * a[i]!
    const prevClose = i > 0 ? bars[i - 1]!.close : b.close

    upper = basicUpper < upper || prevClose > upper ? basicUpper : upper
    lower = basicLower > lower || prevClose < lower ? basicLower : lower
    if (upper === 0) upper = basicUpper
    if (lower === 0) lower = basicLower

    if (trend === 1 && b.close < lower) trend = -1
    else if (trend === -1 && b.close > upper) trend = 1

    line.push(trend === 1 ? lower : upper)
    dir.push(trend)
  })
  return { line, direction: dir }
}

/** On-balance volume — cumulative, so it has no warm-up. */
export function obv(bars: Bar[]): Series {
  let acc = 0
  return bars.map((b, i) => {
    if (i === 0) return 0
    const prev = bars[i - 1]!.close
    if (b.close > prev) acc += b.volume
    else if (b.close < prev) acc -= b.volume
    return acc
  })
}

/** Donchian channel — the n-bar high and low. */
export function donchian(bars: Bar[], n = 20) {
  const upper: Series = []
  const lower: Series = []
  bars.forEach((_, i) => {
    if (i < n - 1) { upper.push(null); lower.push(null); return }
    let hi = -Infinity
    let lo = Infinity
    for (let j = i - n + 1; j <= i; j++) {
      hi = Math.max(hi, bars[j]!.high)
      lo = Math.min(lo, bars[j]!.low)
    }
    upper.push(hi)
    lower.push(lo)
  })
  return { upper, lower }
}

// ---------------------------------------------------------------------------
// Series transforms — alternative bar constructions
//
// These change what a "bar" MEANS, which is why each carries a warning in the
// UI. Heikin-Ashi and Renko prices are derived, not traded: a Renko brick's
// "close" is a threshold the price crossed, not a price anything printed at.
// Reading a stop level off one is a real and common mistake.
// ---------------------------------------------------------------------------

/** Heikin-Ashi: smoothed candles that make trend easier to see and DISTORT the
 *  open and close. The high and low remain real extremes. */
export function heikinAshi(bars: Bar[]): Bar[] {
  const out: Bar[] = []
  bars.forEach((b, i) => {
    const close = (b.open + b.high + b.low + b.close) / 4
    const open = i === 0
      ? (b.open + b.close) / 2
      : (out[i - 1]!.open + out[i - 1]!.close) / 2
    out.push({
      time: b.time,
      open,
      close,
      high: Math.max(b.high, open, close),
      low: Math.min(b.low, open, close),
      volume: b.volume,
    })
  })
  return out
}

/** Renko: fixed-size bricks, plotted only when price moves a full brick.
 *  Time is NOT linear on a Renko chart — several bricks can share a minute and
 *  an hour can produce none — so the timestamps here are the moment each brick
 *  completed, kept strictly increasing so the chart library can plot them. */
export function renko(bars: Bar[], brickSize: number): Bar[] {
  if (bars.length === 0 || brickSize <= 0) return []
  const out: Bar[] = []
  let base = bars[0]!.close
  let lastTime = 0

  for (const b of bars) {
    let moved = b.close - base
    while (Math.abs(moved) >= brickSize) {
      const up = moved > 0
      const open = base
      const close = up ? base + brickSize : base - brickSize
      // Two bricks from one bar would share a timestamp, which most chart
      // engines silently drop. Nudging keeps every brick visible.
      const t = Math.max(b.time, lastTime + 1)
      lastTime = t
      out.push({
        time: t, open, close,
        high: Math.max(open, close), low: Math.min(open, close),
        volume: b.volume,
      })
      base = close
      moved = b.close - base
    }
  }
  return out
}

/** Line break: a new bar only when the close exceeds the extreme of the last
 *  `count` bars. Like Renko, its x-axis is not time. */
export function lineBreak(bars: Bar[], count = 3): Bar[] {
  const out: Bar[] = []
  let lastTime = 0
  for (const b of bars) {
    if (out.length === 0) {
      out.push({ ...b, open: b.open, close: b.close })
      lastTime = b.time
      continue
    }
    const recent = out.slice(-count)
    const hi = Math.max(...recent.map((r) => Math.max(r.open, r.close)))
    const lo = Math.min(...recent.map((r) => Math.min(r.open, r.close)))
    const prev = out[out.length - 1]!
    if (b.close > hi || b.close < lo) {
      const t = Math.max(b.time, lastTime + 1)
      lastTime = t
      out.push({
        time: t, open: prev.close, close: b.close,
        high: Math.max(prev.close, b.close),
        low: Math.min(prev.close, b.close),
        volume: b.volume,
      })
    }
  }
  return out
}

/** A sensible default brick size: one ATR, rounded to something readable.
 *  A hardcoded brick size produces one brick on a ₹50 stock and ten thousand
 *  on an index. */
export function suggestedBrickSize(bars: Bar[]): number {
  const a = atr(bars, 14).filter((x): x is number => x != null)
  if (a.length === 0) {
    const last = bars[bars.length - 1]?.close ?? 100
    return Math.max(last * 0.005, 0.05)
  }
  const v = a[a.length - 1]!
  const mag = 10 ** Math.floor(Math.log10(v))
  return Math.max(Math.round(v / mag) * mag, 0.05)
}
